import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Tabs, Tab,
  Chip, Button, Table, TableHead, TableBody, TableRow, TableCell,
  TextField, MenuItem, Select, FormControl, InputLabel, IconButton,
  Tooltip, Divider, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SyncIcon from '@mui/icons-material/Sync';
import KeyIcon from '@mui/icons-material/Key';
import FactoryIcon from '@mui/icons-material/Factory';
import BusinessIcon from '@mui/icons-material/Business';
import { useParams, useNavigate } from 'react-router-dom';
import { nodeNetworkService } from '../services/nodeNetworkService';
import NodeConnectionManager from './NodeConnectionManager';
import PACTCredentialDialog from './PACTCredentialDialog';
import { NODE_TYPES, NODE_ROLES } from '../constants/nodeTypes';

const NODE_STATUSES = ['active','inactive','suspended','pending'];
const BINDING_STATUSES = ['proposed','verified','bound','revoked'];
const ID_TYPES = ['LEI','VAT','DUNS','registration_number','tax_id','customs_id','other'];

function TabPanel({ children, value, index }) {
  return value === index ? <Box pt={2}>{children}</Box> : null;
}

function NodeDetail() {
  const { nodeId } = useParams();
  const navigate = useNavigate();
  const [node, setNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [addIdentityOpen, setAddIdentityOpen] = useState(false);
  const [identityForm, setIdentityForm] = useState({ identifier_type: 'LEI', identifier_value: '', jurisdiction: '' });
  const [addConnectionOpen, setAddConnectionOpen] = useState(false);
  const [pactLoading, setPactLoading] = useState(false);
  const [pactCredentials, setPactCredentials] = useState(null);
  const [credentialDialogOpen, setCredentialDialogOpen] = useState(false);
  const [ofpLei, setOfpLei] = useState(null);

  useEffect(() => {
    loadNode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  const loadNode = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await nodeNetworkService.getNode(nodeId);
      setNode(data);
      // Resolve the node's OFP organization LEI (Phase 1, GLEIF/vLEI); non-fatal.
      nodeNetworkService.getNodeOfpLei(nodeId).then(setOfpLei).catch(() => setOfpLei(null));
      setEditForm({
        node_name: data.node_name,
        node_type: data.node_type,
        node_role: data.node_role,
        status: data.status,
        pact_node_id: data.pact_node_id || '',
        api_url: data.api_url || '',
        auth_base_url: data.auth_base_url || '',
        scope: data.scope || '',
        audience: data.audience || '',
        spec_version: data.spec_version || '',
        ofp_organization_id: data.ofp_organization_id || '',
        ofp_facility_id: data.ofp_facility_id || '',
        ofp_boundary_id: data.ofp_boundary_id || ''
      });
    } catch (err) {
      setError('Failed to load node: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await nodeNetworkService.updateNode(nodeId, editForm);
      setEditing(false);
      await loadNode();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAddIdentity = async () => {
    try {
      await nodeNetworkService.createLegalIdentity(nodeId, identityForm);
      setAddIdentityOpen(false);
      setIdentityForm({ identifier_type: 'LEI', identifier_value: '', jurisdiction: '' });
      await loadNode();
    } catch (err) {
      setError('Failed to add identity: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleBindingStatusChange = async (lid, newStatus) => {
    try {
      await nodeNetworkService.updateLegalIdentity(nodeId, lid, { binding_status: newStatus });
      await loadNode();
    } catch (err) {
      setError('Failed to update binding status: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteIdentity = async (lid) => {
    if (!window.confirm('Delete this legal identity?')) return;
    try {
      await nodeNetworkService.deleteLegalIdentity(nodeId, lid);
      await loadNode();
    } catch (err) {
      setError('Failed to delete identity: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteConnection = async (connectionId) => {
    if (!window.confirm('Delete this connection?')) return;
    try {
      await nodeNetworkService.deleteConnection(connectionId);
      await loadNode();
    } catch (err) {
      setError('Failed to delete connection: ' + (err.response?.data?.error || err.message));
    }
  };

  const handlePushNodeToPACT = async () => {
    try {
      setPactLoading(true);
      setError(null);
      await nodeNetworkService.pushNodeToPACT(nodeId);
      await loadNode();
    } catch (err) {
      setError('PACT push failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setPactLoading(false);
    }
  };

  const handleSyncNodeFromPACT = async () => {
    try {
      setPactLoading(true);
      setError(null);
      const result = await nodeNetworkService.syncNodeFromPACT(nodeId, true);
      if (result.differences?.length === 0) {
        setError(null);
      }
      await loadNode();
    } catch (err) {
      setError('PACT sync failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setPactLoading(false);
    }
  };

  const handlePushConnectionToPACT = async (connectionId) => {
    try {
      setPactLoading(true);
      setError(null);
      await nodeNetworkService.pushConnectionToPACT(connectionId);
      await loadNode();
    } catch (err) {
      setError('PACT connection push failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setPactLoading(false);
    }
  };

  const handleAcceptPACTConnection = async (connectionId) => {
    try {
      setPactLoading(true);
      setError(null);
      const result = await nodeNetworkService.acceptPACTConnection(connectionId);
      setPactCredentials({ clientId: result.clientId, clientSecret: result.clientSecret, pact_connection_id: result.pact_connection_id });
      setCredentialDialogOpen(true);
      await loadNode();
    } catch (err) {
      setError('PACT accept failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setPactLoading(false);
    }
  };

  const handleRotatePACTCredentials = async (connectionId) => {
    try {
      setPactLoading(true);
      setError(null);
      const result = await nodeNetworkService.rotatePACTCredentials(connectionId);
      setPactCredentials({ clientId: result.clientId, clientSecret: result.clientSecret });
      setCredentialDialogOpen(true);
      await loadNode();
    } catch (err) {
      setError('PACT credential rotation failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setPactLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !node) {
    return <Box p={3}><Alert severity="error">{error}</Alert></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight="bold">{node?.node_name}</Typography>
          {node?.network && (
            <Button
              size="small"
              onClick={() => navigate(`/node-network/${node.network.network_id}/graph`)}
              sx={{ p: 0, mt: 0.5, textTransform: 'none' }}
            >
              ← {node.network.network_name}
            </Button>
          )}
        </Box>
        <Box display="flex" gap={1}>
          {editing ? (
            <>
              <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSave}>Save</Button>
              <Button size="small" startIcon={<CancelIcon />} onClick={() => setEditing(false)}>Cancel</Button>
            </>
          ) : (
            <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditing(true)}>Edit</Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Overview" />
          <Tab label={`Legal Identities (${node?.legalIdentities?.length || 0})`} />
          <Tab label={`Connections (${(node?.outgoingConnections?.length || 0) + (node?.incomingConnections?.length || 0)})`} />
          <Tab label={`Ledgers (${node?.ledgers?.length || 0})`} />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ p: 2 }}>
            {editing ? (
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField label="Node Name" value={editForm.node_name} onChange={e => setEditForm(f => ({ ...f, node_name: e.target.value }))} fullWidth />
                <Box display="flex" gap={2}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select value={editForm.node_type} label="Type" onChange={e => setEditForm(f => ({ ...f, node_type: e.target.value }))}>
                      {NODE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select value={editForm.node_role} label="Role" onChange={e => setEditForm(f => ({ ...f, node_role: e.target.value }))}>
                      {NODE_ROLES.map(r => <MenuItem key={r} value={r}>{r.replace('_', ' ')}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={editForm.status} label="Status" onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                      {NODE_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
                <Divider><Typography variant="caption">PACT Directory Fields</Typography></Divider>
                <TextField label="PACT Node ID" value={editForm.pact_node_id} onChange={e => setEditForm(f => ({ ...f, pact_node_id: e.target.value }))} fullWidth />
                <TextField label="API URL" value={editForm.api_url} onChange={e => setEditForm(f => ({ ...f, api_url: e.target.value }))} fullWidth />
                <Box display="flex" gap={2}>
                  <TextField label="Auth Base URL" value={editForm.auth_base_url} onChange={e => setEditForm(f => ({ ...f, auth_base_url: e.target.value }))} fullWidth />
                  <TextField label="Scope" value={editForm.scope} onChange={e => setEditForm(f => ({ ...f, scope: e.target.value }))} fullWidth />
                </Box>
                <Box display="flex" gap={2}>
                  <TextField label="Audience" value={editForm.audience} onChange={e => setEditForm(f => ({ ...f, audience: e.target.value }))} fullWidth />
                  <TextField label="Spec Version" value={editForm.spec_version} onChange={e => setEditForm(f => ({ ...f, spec_version: e.target.value }))} fullWidth />
                </Box>
                <Divider><Typography variant="caption">OFP Linkage</Typography></Divider>
                <TextField label="OFP Organization ID" value={editForm.ofp_organization_id} onChange={e => setEditForm(f => ({ ...f, ofp_organization_id: e.target.value }))} fullWidth helperText="e.g. org-sunray" />
                <Box display="flex" gap={2}>
                  <TextField label="OFP Facility ID" value={editForm.ofp_facility_id} onChange={e => setEditForm(f => ({ ...f, ofp_facility_id: e.target.value }))} fullWidth helperText="e.g. fac-kerrobert-refinery" />
                  <TextField label="OFP Boundary ID" value={editForm.ofp_boundary_id} onChange={e => setEditForm(f => ({ ...f, ofp_boundary_id: e.target.value }))} fullWidth helperText="e.g. ob-sunray-ops" />
                </Box>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                  <Chip label={node.node_type} size="small" color="primary" />
                  <Chip label={node.node_role?.replace('_', ' ')} size="small" variant="outlined" />
                  <Chip label={node.status} size="small" color={node.status === 'active' ? 'success' : 'default'} />
                </Box>
                {node.pact_node_id && <Typography variant="body2"><strong>PACT Node ID:</strong> {node.pact_node_id}</Typography>}
                {node.api_url && <Typography variant="body2"><strong>API URL:</strong> {node.api_url}</Typography>}
                {node.auth_base_url && <Typography variant="body2"><strong>Auth Base URL:</strong> {node.auth_base_url}</Typography>}
                {node.spec_version && <Typography variant="body2"><strong>Spec Version:</strong> {node.spec_version}</Typography>}
                {node.ofp_organization_id && (
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body2"><strong>OFP Org:</strong> {node.ofp_organization_id}</Typography>
                    {ofpLei?.lei
                      ? <Chip label={`LEI: ${ofpLei.lei}`} size="small" color="success" icon={<BusinessIcon />} />
                      : <Chip label="No LEI" size="small" variant="outlined" />}
                  </Box>
                )}
                {node.parentNode && (
                  <Typography variant="body2">
                    <strong>Parent Node:</strong> {node.parentNode.node_name} ({node.parentNode.node_type})
                  </Typography>
                )}
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>PACT Directory</Typography>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  {node.pact_sync_status === null && (
                    <Button size="small" variant="outlined" startIcon={<CloudUploadIcon />} onClick={handlePushNodeToPACT} disabled={pactLoading}>
                      Push to PACT
                    </Button>
                  )}
                  {node.pact_sync_status === 'synced' && (
                    <>
                      <Chip label={`PACT Synced #${node.pact_node_id}`} size="small" color="success" icon={<SyncIcon />} />
                      <Button size="small" variant="text" startIcon={<CloudDownloadIcon />} onClick={handleSyncNodeFromPACT} disabled={pactLoading}>
                        Sync from PACT
                      </Button>
                    </>
                  )}
                  {node.pact_sync_status === 'pending' && (
                    <Chip label="PACT Pending" size="small" color="warning" />
                  )}
                  {node.pact_sync_status === 'error' && (
                    <>
                      <Chip label="PACT Error" size="small" color="error" />
                      <Typography variant="caption" color="error">{node.pact_sync_error}</Typography>
                      <Button size="small" variant="outlined" startIcon={<CloudUploadIcon />} onClick={handlePushNodeToPACT} disabled={pactLoading}>
                        Retry
                      </Button>
                    </>
                  )}
                  {node.pact_last_synced_at && (
                    <Typography variant="caption" color="text.secondary">
                      Last synced: {new Date(node.pact_last_synced_at).toLocaleString()}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Created: {new Date(node.created_at).toLocaleString()}
                </Typography>

                {/* OFP Footprint Section */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Open Footprint</Typography>
                {node.ofp_facility_id ? (
                  <Paper variant="outlined" sx={{ p: 1.5, borderLeft: '3px solid #00796b' }}>
                    {node.ofp && node.ofp.organization && (
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <BusinessIcon sx={{ fontSize: 18, color: '#00796b' }} />
                        <Typography variant="body2"><strong>Organization:</strong> {node.ofp.organization.name}</Typography>
                      </Box>
                    )}
                    {node.ofp && node.ofp.boundary && (
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="body2" sx={{ ml: 3 }}><strong>Boundary:</strong> {node.ofp.boundary.name}</Typography>
                      </Box>
                    )}
                    {node.ofp && node.ofp.facility && (
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <FactoryIcon sx={{ fontSize: 18, color: '#00796b' }} />
                        <Typography variant="body2"><strong>Facility:</strong> {node.ofp.facility.name}</Typography>
                      </Box>
                    )}
                    {node.ofp && node.ofp.emission_activity_count > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Chip label={`${node.ofp.emission_activity_count} emission activities`} size="small" sx={{ fontSize: '0.7rem' }} />
                        <Chip label={`${node.ofp.emission_statement_count} emission statements`} size="small" sx={{ fontSize: '0.7rem' }} />
                      </Box>
                    )}
                    {(node.ofp?.emission_activities?.length ?? 0) > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Activities:</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {(node.ofp?.emission_activities ?? []).slice(0, 8).map(a => (
                            <Chip key={a.emission_activity_id} label={a.name} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                          ))}
                          {node.ofp.emission_activities.length > 8 && (
                            <Chip label={`+${node.ofp.emission_activities.length - 8} more`} size="small" sx={{ fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                ) : (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2">Not linked to an Open Footprint facility.</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Edit this node to add OFP Organization ID, Facility ID, and Boundary ID.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Legal Identities Tab */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAddIdentityOpen(true)}>
                Add Identity
              </Button>
            </Box>
            {node?.legalIdentities?.length === 0 ? (
              <Typography color="text.secondary" variant="body2">No legal identities bound to this node yet.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Jurisdiction</TableCell>
                    <TableCell>Binding Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {node?.legalIdentities?.map(id => (
                    <TableRow key={id.legal_identity_id}>
                      <TableCell><Chip label={id.identifier_type} size="small" /></TableCell>
                      <TableCell><Typography variant="body2" fontFamily="monospace">{id.identifier_value}</Typography></TableCell>
                      <TableCell>{id.jurisdiction || '—'}</TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={id.binding_status}
                            onChange={e => handleBindingStatusChange(id.legal_identity_id, e.target.value)}
                            variant="standard"
                          >
                            {BINDING_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDeleteIdentity(id.legal_identity_id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </TabPanel>

        {/* Connections Tab */}
        <TabPanel value={tab} index={2}>
          <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAddConnectionOpen(true)}>
                Add Connection
              </Button>
            </Box>
            {(node?.outgoingConnections?.length || 0) + (node?.incomingConnections?.length || 0) === 0 ? (
              <Typography color="text.secondary" variant="body2">No connections for this node.</Typography>
            ) : (
              <>
                {node?.outgoingConnections?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>Outgoing</Typography>
                    <Table size="small" sx={{ mb: 2 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>To Node</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>PACT Connection ID</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {node.outgoingConnections.map(c => (
                          <TableRow key={c.connection_id}>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <ArrowForwardIcon fontSize="small" color="action" />
                                <Button size="small" onClick={() => navigate(`/node-network/nodes/${c.to_node_id}`)} sx={{ p: 0 }}>
                                  {c.toNode?.node_name}
                                </Button>
                              </Box>
                            </TableCell>
                            <TableCell><Chip label={c.connection_type} size="small" /></TableCell>
                            <TableCell><Chip label={c.status} size="small" color={c.status === 'active' ? 'success' : 'default'} /></TableCell>
                            <TableCell><Typography variant="caption" fontFamily="monospace">{c.pact_connection_id || '—'}</Typography></TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                {c.pact_sync_status === null && !c.pact_connection_id && (
                                  <Tooltip title="Push to PACT Directory">
                                    <IconButton size="small" onClick={() => handlePushConnectionToPACT(c.connection_id)} disabled={pactLoading}>
                                      <CloudUploadIcon fontSize="small" color="primary" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {c.pact_sync_status === 'pending' && (
                                  <Tooltip title="Accept PACT Invitation">
                                    <IconButton size="small" onClick={() => handleAcceptPACTConnection(c.connection_id)} disabled={pactLoading}>
                                      <KeyIcon fontSize="small" color="warning" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {c.pact_sync_status === 'synced' && c.pact_connection_id && (
                                  <Tooltip title="Rotate PACT Credentials">
                                    <IconButton size="small" onClick={() => handleRotatePACTCredentials(c.connection_id)} disabled={pactLoading}>
                                      <SyncIcon fontSize="small" color="success" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {c.pact_sync_status === 'error' && (
                                  <Tooltip title={`PACT Error: ${c.pact_sync_error}. Retry push.`}>
                                    <IconButton size="small" onClick={() => handlePushConnectionToPACT(c.connection_id)} disabled={pactLoading}>
                                      <CloudUploadIcon fontSize="small" color="error" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Delete Connection">
                                  <IconButton size="small" onClick={() => handleDeleteConnection(c.connection_id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
                {node?.incomingConnections?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>Incoming</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>From Node</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {node.incomingConnections.map(c => (
                          <TableRow key={c.connection_id}>
                            <TableCell>
                              <Button size="small" onClick={() => navigate(`/node-network/nodes/${c.from_node_id}`)} sx={{ p: 0 }}>
                                {c.fromNode?.node_name}
                              </Button>
                            </TableCell>
                            <TableCell><Chip label={c.connection_type} size="small" /></TableCell>
                            <TableCell><Chip label={c.status} size="small" color={c.status === 'active' ? 'success' : 'default'} /></TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => handleDeleteConnection(c.connection_id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </>
            )}
          </Box>
        </TabPanel>

        {/* Ledgers Tab */}
        <TabPanel value={tab} index={3}>
          <Box sx={{ p: 2 }}>
            {node?.ledgers?.length === 0 ? (
              <Typography color="text.secondary" variant="body2">No ledgers linked to this node.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ledger Name</TableCell>
                    <TableCell>Entity Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total Debits (kgCO2e)</TableCell>
                    <TableCell align="right">Balance (kgCO2e)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {node?.ledgers?.map(l => (
                    <TableRow key={l.e_ledger_id}>
                      <TableCell>{l.ledger_name}</TableCell>
                      <TableCell>{l.entity_type}</TableCell>
                      <TableCell><Chip label={l.status} size="small" color={l.status === 'active' ? 'success' : 'default'} /></TableCell>
                      <TableCell align="right">{parseFloat(l.total_debit_kg_co2e || 0).toLocaleString()}</TableCell>
                      <TableCell align="right">{parseFloat(l.balance_kg_co2e || 0).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Add Legal Identity Dialog */}
      <Dialog open={addIdentityOpen} onClose={() => setAddIdentityOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Legal Identity</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <FormControl fullWidth>
              <InputLabel>Identifier Type</InputLabel>
              <Select
                value={identityForm.identifier_type}
                label="Identifier Type"
                onChange={e => setIdentityForm(f => ({ ...f, identifier_type: e.target.value }))}
              >
                {ID_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Identifier Value"
              value={identityForm.identifier_value}
              onChange={e => setIdentityForm(f => ({ ...f, identifier_value: e.target.value }))}
              required fullWidth
              placeholder={identityForm.identifier_type === 'LEI' ? '20-char LEI code' : 'Identifier value'}
            />
            <TextField
              label="Jurisdiction (ISO 3166)"
              value={identityForm.jurisdiction}
              onChange={e => setIdentityForm(f => ({ ...f, jurisdiction: e.target.value }))}
              fullWidth
              placeholder="e.g. CA, US, NL"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddIdentityOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddIdentity} disabled={!identityForm.identifier_value.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Connection Dialog */}
      <NodeConnectionManager
        open={addConnectionOpen}
        onClose={() => setAddConnectionOpen(false)}
        nodeId={nodeId}
        networkId={node?.network_id}
        onCreated={loadNode}
      />

      {/* PACT Credential Display Dialog (one-time) */}
      <PACTCredentialDialog
        open={credentialDialogOpen}
        onClose={() => setCredentialDialogOpen(false)}
        credentials={pactCredentials}
      />
    </Box>
  );
}

export default NodeDetail;
